<template>
  <!-- Image grid -->
  <div :class="['flex flex-wrap gap-4', fullWidth ? 'w-full':'']" ref="rootEl">
    <div :class="['flex flex-wrap gap-4' , fullWidth ? 'w-full':'']">
      <div
          v-for="(slot, idx) in slots"
          :key="idx"
          :class="`${ slot.status === 'success' && !slot.removed ? 'border-solid' : 'border-dashed'} ${required ? 'border-red-300' : 'border-slate-400'} ${slot.status === 'uploading' ? 'animate-pulse' : ''} ${fullWidth? 'w-full' : ''}`"
          class="relative rounded-xl border-[2px] flex flex-col items-center"
      >
        <!-- hidden native input -->
        <input
            type="file"
            accept="image/*"
            class="hidden"
            :id="`${id}-${idx}`"
            @change="e => onFileChange(e, idx)"
        />

        <!-- preview / placeholder -->
        <label
            :for="`${id}-${idx}`"
            :class="['cursor-pointer flex items-center justify-center rounded-md overflow-hidden', sizeClass]"
        >
          <img
              v-if="slot.url"
              :src="slot.url"
              alt="preview"
              :class="['w-full h-full', coverImage ? 'object-cover' : 'object-contain']"
          />
          <span v-else :class="['text-gray-400 text-center', fontSize]">Select file or Paste</span>
        </label>

        <!-- action buttons -->
        <div class="absolute top-1 right-1 flex gap-1">
          <!-- status indicator -->
          <div class="flex justify-center gap-2">
            <!-- success -->
            <button
                v-if="slot.status === 'success' && !slot.removed"
                class="p-0.5 rounded-full shadow bg-green-700 cursor-default"
                disabled
                title="uploaded"
            >
              <svg
                  data-v-a0ffc7b3="" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                  aria-hidden="true" role="img" class="cursor-pointer text-white iconify iconify--icon-park-outline"
                  width="20"
                  height="20" viewBox="0 0 48 48">
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="4">
                  <path
                      d="M12 33c-3.333 0-8-1.5-8-7.5c0-7 7-8.5 9-8.5c1-3.5 3-9 11-9c7 0 10 4 11 7.5c0 0 9 1 9 9.5c0 6-4 8-8 8"></path>
                  <path d="m18 33l6 5l8-10"></path>
                </g>
              </svg>
            </button>

            <!-- uploading -->
            <button
                v-else-if="slot.status === 'uploading'"
                @click="cancelUpload(idx)"
                class="p-0.5 rounded-full shadow bg-blue-500 text-white"
                title="cancel upload"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                  <path stroke-dasharray="2 4" stroke-dashoffset="6"
                        d="M12 21c-4.97 0 -9 -4.03 -9 -9c0 -4.97 4.03 -9 9 -9">
                    <animate attributeName="stroke-dashoffset" dur="0.6s" repeatCount="indefinite" values="6;0"/>
                  </path>
                  <path stroke-dasharray="32" stroke-dashoffset="32" d="M12 3c4.97 0 9 4.03 9 9c0 4.97 -4.03 9 -9 9">
                    <animate fill="freeze" attributeName="stroke-dashoffset" begin="0.1s" dur="0.4s" values="32;0"/>
                  </path>
                  <path stroke-dasharray="10" stroke-dashoffset="10" d="M12 16v-7.5">
                    <animate fill="freeze" attributeName="stroke-dashoffset" begin="0.5s" dur="0.2s" values="10;0"/>
                  </path>
                  <path stroke-dasharray="6" stroke-dashoffset="6" d="M12 8.5l3.5 3.5M12 8.5l-3.5 3.5">
                    <animate fill="freeze" attributeName="stroke-dashoffset" begin="0.7s" dur="0.2s" values="6;0"/>
                  </path>
                </g>
              </svg>
            </button>

            <!-- error -->
            <button
                v-else-if="slot.status === 'error'"
                @click="retryUpload(idx)"
                class="p-0.5 rounded-full shadow bg-red-600 text-white"
                title="retry"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 12v4m0 4h.01M17 18h.5a1 1 0 0 0 0-9h-1.79A7 7 0 1 0 7 17.708"/>
              </svg>
            </button>

            <!-- removed marker: Only when the original image existed -->
            <button
                v-else-if="slot.removed && slot.fromBackend"
                class="p-0.5 rounded-full shadow bg-white text-slate-900 cursor-default"
                disabled
                title="removed"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M5 12h14"/>
              </svg>
            </button>
          </div>

          <!-- view -->
          <button
              v-if="slot.url && !slot.removed"
              @click="openViewer(idx)"
              class="p-0.5 rounded-full shadow bg-white h-max"
              title="watch"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 512 512">
              <circle cx="256" cy="256" r="64" fill="#2a2496"/>
              <path fill="#2a2496"
                    d="M394.82 141.18C351.1 111.2 304.31 96 255.76 96c-43.69 0-86.28 13-126.59 38.48C88.52 160.23 48.67 207 16 256c26.42 44 62.56 89.24 100.2 115.18C159.38 400.92 206.33 416 255.76 416c49 0 95.85-15.07 139.3-44.79C433.31 345 469.71 299.82 496 256c-26.38-43.43-62.9-88.56-101.18-114.82M256 352a96 96 0 1 1 96-96a96.11 96.11 0 0 1-96 96"/>
            </svg>
          </button>

          <!-- delete -->
          <button
              v-if="(slot.url || slot.file) && !slot.removed"
              @click="removeImage(idx)"
              class="p-0.5 rounded-full shadow bg-white h-max"
              title="delete"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
              <path fill="red"
                    d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zm2-4h2V8H9zm4 0h2V8h-2z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <VueEasyLightbox
      :visible="viewerVisible"
      :imgs="slots.map(s => s.url).filter(Boolean)"
      :index="viewerIndex"
      @hide="viewerVisible = false"
  />
</template>

<script setup>
import {ref, reactive, watch, computed, onMounted, onBeforeUnmount} from 'vue'
import VueEasyLightbox from 'vue-easy-lightbox'
import {useUploadStore} from '@/stores/upload.js'

const uploadStore = useUploadStore()
const MAX_AUTO_RETRIES = 2

const props = defineProps({
  count: {type: Number, default: 5},
  backendImages: {type: Array, default: () => []},
  onlyChanges: {type: Boolean, default: false},
  uploading: {type: Boolean, default: false},
  sizeClass: {type: String, default: 'w-32 h-32'},
  fullWidth: {type: Boolean, default: false},
  coverImage: {type: Boolean, default: false},
  fontSize: {type: String, default: 'text-sm'},
  id: {type: String, default: 'file'},
  required: {type: Boolean, default: false}
})
const emit = defineEmits(['update:images', 'update:payload', 'delete', 'update:uploading'])

// ---------- state ---------- //
const slots = reactive([])
const rootEl = ref(null)
const viewerVisible = ref(false)
const viewerIndex = ref(0)

function initSlots() {
  slots.length = 0
  const pre = props.backendImages.slice(0, props.count)
  pre.forEach(img => {
    const link = img.link || ''
    slots.push({
      url: link,
      status: link ? 'success' : 'idle',
      name: img.name || '',
      removed: false,
      file: undefined,
      abortController: undefined,
      attempts: 0,
      fromBackend: !!link,
      uploadId: null, // Current upload ID
    })
  })
  for (let i = pre.length; i < props.count; i++) {
    slots.push({url: '', status: 'idle', removed: false, attempts: 0, fromBackend: false, uploadId: null})
  }
}

initSlots()
watch(() => props.count, initSlots)

// ---------- uploading state ---------- //
const isUploading = computed(() => slots.some(s => s.status === 'uploading'))
watch(isUploading, (uploading) => emit('update:uploading', uploading), {immediate: true})

const originHasImage = idx => !!(props.backendImages[idx]?.link)
const firstEmptySlot = () => slots.findIndex(s => !s.url && !s.file && !s.removed && s.status === 'idle')

function addFileToSlot(file, fileName, idx) {
  const url = URL.createObjectURL(file)
  const abortController = new AbortController()
  const uploadId = Symbol('upload') // upload Id
  slots[idx] = {
    ...slots[idx],
    file,
    url,
    status: 'uploading',
    abortController,
    name: fileName,
    removed: false,
    attempts: 0,
    fromBackend: false,
    uploadId
  }
  runUpload(file, fileName, idx, uploadId)
}

async function runUpload(file, fileName, idx, uploadId) {
  try {
    const result = await uploadStore.uploadFile({fileName, file}, {returnResponse: true})
    if (slots[idx].uploadId !== uploadId) return // Slot canceled or replaced in the middle of uploading

    if (result?.success) {
      slots[idx].name = result.data.file.name
      slots[idx].status = 'success'
    } else {
      if (slots[idx].attempts < MAX_AUTO_RETRIES) {
        slots[idx].attempts++
        console.log(`Automatic Retry ${slots[idx].attempts} for Slot ${idx}`)
        await runUpload(file, fileName, idx, uploadId)
      } else {
        slots[idx].status = 'error'
      }
    }
  } catch (err) {
    if (slots[idx].uploadId !== uploadId) return // canceled
    if (err?.name === 'AbortError') return
    slots[idx].status = 'error'
    console.error('Error in upload:', err)
  } finally {
    emit('update:images', slots)
  }
}

// ---------- UI event handlers ---------- //
function onFileChange(e, idx) {
  const file = e.target.files?.[0]
  if (file) addFileToSlot(file, file.name, idx)
}

function cancelUpload(idx) {
  slots[idx].abortController?.abort()
  slots[idx] = {...slots[idx], url: '', status: 'idle', file: undefined, removed: false, attempts: 0, uploadId: null}
  emit('update:images', slots)
}

function retryUpload(idx) {
  const file = slots[idx].file
  if (!file) return
  const abortController = new AbortController()
  const uploadId = Symbol('upload')
  slots[idx] = {...slots[idx], status: 'uploading', abortController, attempts: 0, uploadId}
  runUpload(file, file.name, idx, uploadId)
}

function removeImage(idx) {
  const wasBackend = slots[idx].fromBackend
  slots[idx].abortController?.abort()
  slots[idx] = {
    ...slots[idx],
    url: '',
    file: undefined,
    status: 'idle',
    removed: wasBackend,
    attempts: 0,
    uploadId: null
  }
  emit('delete', {idx})
  emit('update:images', slots)
}

const openViewer = idx => {
  viewerIndex.value = idx;
  viewerVisible.value = true
}

// ---------- PASTE handling (global registry + smart routing) ----------
/**
 * registry entry: { id, rootEl (ref), handlePaste (fn) }
 * global listener routes the event to:
 *  1) the handler that contains the event target (via composedPath)
 *  2) otherwise the last visible handler in registry (from end)
 */
const instanceId = `${props.id}-${Math.random().toString(36).slice(2, 9)}`

function localHandlePaste(e) {
  // same logic as your original handlePaste but local to this instance
  const fileName = `${Date.now()}.png`
  const items = e.clipboardData?.items || []
  for (const item of items) {
    if (item.type && item.type.startsWith && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (!file) continue
      const idx = firstEmptySlot()
      if (idx === -1) {
        console.log('There is no space for the image.')
        return
      }
      addFileToSlot(file, fileName, idx)
      e.preventDefault()
      return
    }
  }
}

// ensure global registry + listener exist
if (!window.__ATTACHMENT_PASTE_REGISTRY__) {
  window.__ATTACHMENT_PASTE_REGISTRY__ = []
  window.__ATTACHMENT_PASTE_GLOBAL__ = function globalPasteListener(e) {
    const registry = window.__ATTACHMENT_PASTE_REGISTRY__
    if (!registry.length) return

    // 1) try composedPath to find containing rootEl
    let path = []
    try {
      path = e.composedPath ? e.composedPath() : (function buildPath(t) {
        const arr = []
        let el = t
        while (el) {
          arr.push(el)
          el = el.parentNode || el.host || (el.defaultView && el.defaultView.frameElement)
        }
        return arr
      })(e.target)
    } catch (err) {
      path = [e.target]
    }

    for (const node of path) {
      if (!node || node === window || node === document) continue
      const found = registry.find(entry => {
        try {
          return entry.rootEl && entry.rootEl.value && entry.rootEl.value.contains(node)
        } catch (_e) {
          return false
        }
      })
      if (found) {
        try {
          found.handlePaste(e)
        } catch (err) {
          console.error(err)
        }
        return
      }
    }

    // 2) fallback: last visible handler
    for (let i = registry.length - 1; i >= 0; i--) {
      const entry = registry[i]
      const el = entry.rootEl?.value
      if (!el || !el.isConnected) continue
      const rect = el.getBoundingClientRect()
      const style = getComputedStyle(el)
      const visible = rect.width > 0 || rect.height > 0
      if (visible && style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
        try {
          entry.handlePaste(e)
        } catch (err) {
          console.error(err)
        }
        return
      }
    }

    // 3) nothing matched -> call the last registered anyway
    try {
      registry[registry.length - 1]?.handlePaste?.(e)
    } catch (err) {
      console.error(err)
    }
  }
  window.addEventListener('paste', window.__ATTACHMENT_PASTE_GLOBAL__)
}

onMounted(() => {
  // register this instance
  window.__ATTACHMENT_PASTE_REGISTRY__.push({
    id: instanceId,
    rootEl,
    handlePaste: localHandlePaste
  })
})

onBeforeUnmount(() => {
  const registry = window.__ATTACHMENT_PASTE_REGISTRY__ || []
  const idx = registry.findIndex(r => r.id === instanceId)
  if (idx !== -1) registry.splice(idx, 1)
  // if registry empty, cleanup global listener
  if (!registry.length && window.__ATTACHMENT_PASTE_GLOBAL__) {
    window.removeEventListener('paste', window.__ATTACHMENT_PASTE_GLOBAL__)
    delete window.__ATTACHMENT_PASTE_GLOBAL__
    delete window.__ATTACHMENT_PASTE_REGISTRY__
  }
})

// expose optional method to manually bring this instance to top (useful if you open modal with v-show)
function activatePastePriority() {
  const registry = window.__ATTACHMENT_PASTE_REGISTRY__ || []
  const idx = registry.findIndex(r => r.id === instanceId)
  if (idx !== -1) {
    const [item] = registry.splice(idx, 1)
    registry.push(item)
  }
}

defineExpose({activatePastePriority})

// ---------- computed payload ----------
const fullPayload = computed(() =>
    slots.map((s, idx) => {
      const origHas = originHasImage(idx)
      if (s.removed && origHas) return {name: s.name || '', size: 0, action: 'remove'}
      if (s.file && s.status === 'success') return {name: s.name, size: 0, action: 'replace'}
      return {size: 0, action: 'none'}
    })
)
const trimPayload = arr => {
  let last = arr.length - 1
  while (last >= 0 && arr[last].action === 'none') last--
  return last < 0 ? [] : arr.slice(0, last + 1)
}
const effectivePayload = computed(() => props.onlyChanges ? trimPayload(fullPayload.value) : fullPayload.value)
watch(effectivePayload, v => emit('update:payload', v), {immediate: true})

</script>